import type {
  ProductsQueryQuery,
  SearchProductsByGtinQuery,
} from "@/api/graphql/generates";
import { Link } from "@/components/Link";
import {
  RightIcon,
  SearchProductItemImage,
  SearchSectionItemTitle,
  SearchSectionItemWrapper,
  TitleWrapper,
} from "@/components/Search/SearchProducts/style";
import { TEST_IDS } from "@/constants/testIds";
import type { Unpacked } from "@/utils/types";
import { SvgArrowRight } from "@getgrover/ui";

type Product =
  | Unpacked<ProductsQueryQuery["searchProducts"]["products"]>
  | Unpacked<SearchProductsByGtinQuery["searchProductsByGtin"]>;

interface SearchSectionItemProps {
  product: Product;
  query: string;
}

/**
 * Render title with highlighted text
 * @param text
 */
const renderTitle = (text: string) => {
  return text.split(/(<mark>.*?<\/mark>)/g).map((part, index) => {
    if (part.startsWith("<mark>") && part.endsWith("</mark>")) {
      const highlightedText = part.replace(/<\/?mark>/g, "");
      return <mark key={`${index}-${highlightedText}`}>{highlightedText}</mark>;
    }
    return <span key={`${index}-${part}`}>{part}</span>;
  });
};

export const SearchProductItem = ({ product }: SearchSectionItemProps) => {
  if (!product) {
    return null;
  }
  return (
    <SearchSectionItemWrapper
      data-testid={`${TEST_IDS.SEARCH_LIST_PRODUCT_ITEM}-${product.id}`}
    >
      <Link
        href={`/products/${product.slug}${"variants" in product ? `?variant=${product.variants[0]?.sku}` : ""}`}
      >
        {product.imageUrl && (
          <SearchProductItemImage
            src={product.imageUrl}
            width={32}
            height={32}
            alt={product.name}
            loading="lazy"
          />
        )}
        <TitleWrapper>
          <SearchSectionItemTitle>
            {renderTitle(product.name)}
          </SearchSectionItemTitle>
        </TitleWrapper>
        <RightIcon>
          <SvgArrowRight />
        </RightIcon>
      </Link>
    </SearchSectionItemWrapper>
  );
};
